/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  CommonApplication,
  CommonApplicationGroup,
  CommonScreentime,
  CommonScreentimeLimit,
} from '@codidae/common-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Button from 'components/button/button';
import Flex from 'components/flex/flex';
import Modal from 'components/modal/modal';
import TimePicker from 'components/time-picker/time-picker';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BiChevronDown, BiChevronRight, BiInfinite, BiTrash } from 'react-icons/bi';
import { useSearchParams } from 'react-router-dom';
import {
  createScreentimeLimit,
  deleteScreentimeLimit,
  getScreentimeForPeriod,
  getScreentimeLimits,
  updateScreentimeLimit,
} from 'services/screentime';
import styles from './app-group-screentime.module.scss';

interface P {
  childrenId: string;
  onBack: () => void;
}

interface TimePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (minutes: number) => void;
  initialMinutes?: number;
  title: string;
}

const TimePickerModal = ({ isOpen, onClose, onSave, initialMinutes = 60, title }: TimePickerModalProps) => {
  const [minutes, setMinutes] = useState(initialMinutes);

  const handleSave = () => {
    onSave(minutes);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className={styles.modalContent}>
        <h2>{title}</h2>
        <TimePicker
          options={{
            hours: Array.from({ length: 24 }, (_, i) => String(i)),
            minutes: Array.from({ length: 60 }, (_, i) => String(i)),
          }}
          value={minutes}
          onChange={setMinutes}
          labeled
          placeholder="Sélectionner la durée"
          tooltipInfo="Durée maximale d'utilisation de l'application par jour"
          editMode
          onEdit={() => {}}
          displayValue={(value) => {
            const hours = Math.floor(value / 60);
            const mins = value % 60;
            return `${hours}h ${mins}min`;
          }}
        />
        <div className={styles.modalButtons}>
          <Button onClick={handleSave}>Valider</Button>
          <Button onClick={onClose} outline>
            Annuler
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export function AppGroupScreentime({ childrenId, onBack }: P) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [timePickerModal, setTimePickerModal] = useState<{
    isOpen: boolean;
    type: 'app' | 'group';
    id: number;
    name: string;
    initialMinutes?: number;
    limitId?: number;
  }>({
    isOpen: false,
    type: 'app',
    id: 0,
    name: '',
  });
  const [searchParams] = useSearchParams();

  const queryClient = useQueryClient();

  const { data: screentimeLimits } = useQuery({
    queryKey: ['getScreentimeLimits', childrenId],
    queryFn: () => getScreentimeLimits(Number(childrenId)),
  });

  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  const { data: screentimes } = useQuery({
    queryKey: ['getScreentimeForPeriod', childrenId],
    queryFn: () => getScreentimeForPeriod(Number(childrenId), lastMonth, new Date()),
  });

  const createLimitMutation = useMutation({
    mutationFn: createScreentimeLimit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getScreentimeLimits', childrenId] });
    },
  });

  const updateLimitMutation = useMutation({
    mutationFn: updateScreentimeLimit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getScreentimeLimits', childrenId] });
    },
  });

  const deleteLimitMutation = useMutation({
    mutationFn: deleteScreentimeLimit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getScreentimeLimits', childrenId] });
    },
  });

  // merge all screentimes into one array
  const mergedScreentimes = screentimes?.reduce((acc, screentime) => {
    const { screentimes: sc } = screentime;
    if (sc) {
      return [...acc, ...sc];
    }
    return acc;
  }, [] as CommonScreentime[]);

  // group by group, and then by app inside
  const groupedScreentimes = useMemo(
    () =>
      mergedScreentimes?.reduce(
        (acc, screentime) => {
          const group = screentime.application.groups[0]?.id;
          const app = screentime.application.id;
          if (!acc[group]) {
            acc[group] = {};
          }
          if (!acc[group][app]) {
            acc[group][app] = screentime.application;
          }
          return acc;
        },
        {} as Record<string, Record<string, CommonApplication>>,
      ),
    [mergedScreentimes],
  );

  const groups = useMemo(
    () =>
      mergedScreentimes?.reduce(
        (acc, screentime) => {
          const group = screentime.application.groups[0]?.id;
          if (!acc[group]) {
            acc[group] = screentime.application.groups[0];
          }
          return acc;
        },
        {} as Record<string, CommonApplicationGroup>,
      ),
    [mergedScreentimes],
  );

  const toggleGroupExpansion = (groupId: string, event: React.MouseEvent) => {
    event.preventDefault();
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const handleOpenTimePicker = (
    type: 'app' | 'group',
    id: number,
    name: string,
    currentLimit?: CommonScreentimeLimit,
  ) => {
    setTimePickerModal({
      isOpen: true,
      type,
      id,
      name,
      initialMinutes: currentLimit?.duration || 60,
      limitId: currentLimit?.id,
    });
  };

  const handleCloseTimePicker = () => {
    setTimePickerModal({
      ...timePickerModal,
      isOpen: false,
    });
    searchParams.delete('applicationId');
    searchParams.delete('groupId');
    searchParams.delete('tab');
    window.history.replaceState({}, '', `?${searchParams.toString()}`);
  };

  const handleSaveTime = (minutes: number) => {
    const { type, id, limitId } = timePickerModal;

    if (limitId) {
      // Update existing limit
      updateLimitMutation.mutate({
        childId: Number(childrenId),
        id: limitId,
        duration: minutes * 60,
      });
    } else {
      // Create new limit
      createLimitMutation.mutate({
        childId: Number(childrenId),
        duration: minutes * 60,
        [type === 'app' ? 'applicationId' : 'groupId']: id,
        priority: 1,
      });
    }
  };

  const handleDeleteLimit = (limitId: number) => {
    deleteLimitMutation.mutate({
      childId: Number(childrenId),
      id: limitId,
    });
  };

  // Find limit for app or group
  const findLimit = useCallback(
    (type: 'app' | 'group', id: number): CommonScreentimeLimit | undefined => {
      if (!screentimeLimits) return undefined;

      return screentimeLimits.find(
        (limit) =>
          (type === 'app' && limit.application && limit.application.id === id) ||
          (type === 'group' && limit.group && limit.group.id === id),
      );
    },
    [screentimeLimits],
  );

  // Format duration in minutes to hours and minutes
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours === 0 && minutes !== 0) {
      return `${minutes}min`;
    }
    return `${hours}h ${minutes}min`;
  };

  useEffect(() => {
    let applicationId = searchParams.get('applicationId');
    let groupId = searchParams.get('groupId');

    applicationId = applicationId === 'undefined' ? null : applicationId;
    groupId = groupId === 'undefined' ? null : groupId;

    if (applicationId || groupId) {
      const type = applicationId ? 'app' : 'group';
      const id = Number(applicationId || groupId);
      const name = applicationId
        ? mergedScreentimes?.find((st) => st.application.id === id)?.application.name || ''
        : groups?.[String(id)]?.name || '';

      const currentLimit = findLimit(type, id);
      handleOpenTimePicker(type, id, name, currentLimit);
    }
  }, [searchParams, mergedScreentimes, groups, findLimit]);

  return (
    <>
      <Flex
        width="100%"
        direction="column"
        gap={15}
        height="100%"
        pad={20}
        style={{
          overflow: 'auto',
        }}
      >
        <div className={styles.header}>Limites de temps d&apos;écran</div>
        <Flex width="100%" direction="column" gap={10} className={styles.weekSettings}>
          {Object.entries(groupedScreentimes || {}).map(([group, apps]) => (
            <div key={group} className={styles.groupContainer}>
              <div className={styles.groupHeader} onClick={(e) => toggleGroupExpansion(group, e)}>
                <div className={styles.groupHeaderContent}>
                  <div className={styles.expandIcon}>
                    {expandedGroups[group] ? <BiChevronDown size={20} /> : <BiChevronRight size={20} />}
                  </div>
                  <h3>{groups?.[group]?.name}</h3>
                </div>
                <div
                  className={styles.timeLimit}
                  onClick={(e) => {
                    e.stopPropagation();
                    const groupLimit = findLimit('group', Number(group));
                    handleOpenTimePicker('group', Number(group), groups?.[group]?.name || '', groupLimit);
                  }}
                >
                  {(() => {
                    const groupLimit = findLimit('group', Number(group));
                    if (!groupLimit) {
                      return <BiInfinite size={24} />;
                    }
                    return (
                      <div className={styles.limitWithDelete}>
                        <span>{formatDuration(groupLimit.duration)}</span>
                        <button
                          className={styles.deleteButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLimit(groupLimit.id);
                          }}
                          type="button"
                        >
                          <BiTrash />
                        </button>
                      </div>
                    );
                  })()}
                </div>
              </div>
              {expandedGroups[group] && (
                <ul className={styles.appList}>
                  {Object.values(apps).map((app) => (
                    <li key={app.id} className={styles.appItem}>
                      <div className={styles.appInfo}>
                        <div className={styles.appIcon}>
                          {app.icon && <img src={`data:image/png;base64,${app.icon}`} alt={app.name} />}
                        </div>
                        <span>{app.name}</span>
                      </div>
                      <div
                        className={styles.timeLimit}
                        onClick={() => {
                          const appLimit = findLimit('app', app.id);
                          handleOpenTimePicker('app', app.id, app.name, appLimit);
                        }}
                      >
                        {(() => {
                          const appLimit = findLimit('app', app.id);
                          if (!appLimit) {
                            return <BiInfinite size={24} />;
                          }
                          return (
                            <div className={styles.limitWithDelete}>
                              <span>{formatDuration(appLimit.duration)}</span>
                              <button
                                className={styles.deleteButton}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteLimit(appLimit.id);
                                }}
                                type="button"
                              >
                                <BiTrash />
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </Flex>
      </Flex>
      <Flex
        gap={15}
        direction="column"
        width="100%"
        pad={15}
        style={{ background: 'var(--primary100)', borderTop: 'solid 3px var(--primary200)' }}
      >
        <Button
          onClick={() => {
            onBack();
          }}
          outline
        >
          RETOUR
        </Button>
      </Flex>

      <TimePickerModal
        isOpen={timePickerModal.isOpen}
        onClose={handleCloseTimePicker}
        onSave={handleSaveTime}
        initialMinutes={timePickerModal.initialMinutes}
        title={`Limite de temps pour ${timePickerModal.name}`}
      />

      {createLimitMutation.isPending || updateLimitMutation.isPending || deleteLimitMutation.isPending ? (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingSpinner} />
        </div>
      ) : null}
    </>
  );
}
